import type { Layer } from '@geosoft/viewer-sdk';
import type { IconName } from '@/design/primitives/Icon';
import type { ChipVariant } from '@/design/primitives/Chip';

export interface LayerStatus {
  key:
    | 'mixed-crs'
    | 'low-confidence'
    | 'expired-credentials'
    | 'restricted'
    | 'partial-loading'
    | 'stale'
    | 'low-accuracy';
  label: string;
  icon: IconName;
  variant: ChipVariant;
  detail?: string;
}

export interface ProviderSourceOption {
  id: string;
  label: string;
  icon: IconName;
}

export const PROVIDER_SOURCE_OPTIONS: ReadonlyArray<ProviderSourceOption> = [
  { id: 'openstreetmap', label: 'OpenStreetMap', icon: 'public' },
  { id: 'maplibre', label: 'MapLibre Style', icon: 'map' },
  { id: 'mapbox', label: 'Mapbox', icon: 'layers' },
  { id: 'arcgis', label: 'ArcGIS', icon: 'map' },
  { id: 'google-pr3d', label: 'Google Photorealistic 3D', icon: 'view_in_ar' },
  { id: 'cesium-ion', label: 'Cesium ion', icon: 'terrain' },
  { id: 'wms', label: 'WMS', icon: 'layers' },
  { id: 'wmts', label: 'WMTS', icon: 'layers' },
  { id: 'wfs', label: 'WFS', icon: 'layers' },
  { id: 'pmtiles', label: 'PMTiles', icon: 'map' },
  { id: 'cog', label: 'COG / GeoTIFF', icon: 'image_not_supported' },
  { id: 'geojson', label: 'GeoJSON', icon: 'description' },
  { id: 'kml', label: 'KML', icon: 'description' },
  { id: 'mbtiles', label: 'MBTiles', icon: 'map' },
] as const;

function readLayerStyleString(layer: Layer, key: string): string | null {
  const value = layer.style?.[key];
  return typeof value === 'string' ? value : null;
}

function readLayerStyleNumber(layer: Layer, key: string): number | null {
  const value = layer.style?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

function sourceUrls(layer: Layer): string[] {
  return Object.values(layer.endpoints).filter((v): v is string => typeof v === 'string');
}

export function providerIdFromLayer(layer: Layer): string {
  const styleProvider = readLayerStyleString(layer, 'providerId');
  if (styleProvider) return styleProvider;
  const urls = sourceUrls(layer).map((u) => u.toLowerCase());
  if (urls.some((u) => u.includes('google'))) return 'google-pr3d';
  if (urls.some((u) => u.includes('mapbox'))) return 'mapbox';
  if (urls.some((u) => u.includes('cesium'))) return 'cesium-ion';
  if (urls.some((u) => u.includes('arcgis') || u.includes('esri'))) return 'arcgis';
  if (urls.some((u) => u.includes('openstreetmap') || u.includes('osm'))) return 'openstreetmap';
  if (urls.some((u) => u.includes('pmtiles'))) return 'pmtiles';
  if (urls.some((u) => u.includes('wms'))) return 'wms';
  if (urls.some((u) => u.includes('wmts'))) return 'wmts';
  if (urls.some((u) => u.includes('wfs'))) return 'wfs';
  if (urls.some((u) => u.includes('.tif') || u.includes('cog'))) return 'cog';
  return 'maplibre';
}

export function providerLabel(id: string): string {
  return PROVIDER_SOURCE_OPTIONS.find((option) => option.id === id)?.label ?? 'Custom';
}

export function providerIcon(id: string): IconName {
  return PROVIDER_SOURCE_OPTIONS.find((option) => option.id === id)?.icon ?? 'extension';
}

export function isLayerRestricted(layer: Layer): boolean {
  const classification = layer.permissions.classification;
  if (classification === 'restricted' || classification === 'classified') return true;
  const styleRestricted = readLayerStyleString(layer, 'visibilityState');
  return styleRestricted === 'restricted';
}

export function isLayerExpiredCredentials(layer: Layer): boolean {
  const health = readLayerStyleString(layer, 'credentialHealth');
  if (health === 'expired' || health === 'expired-credentials') return true;
  const marker = sourceUrls(layer).join('|').toLowerCase();
  return marker.includes('expired') || marker.includes('auth_error');
}

export function layerConfidence(layer: Layer): number | null {
  return readLayerStyleNumber(layer, 'confidence') ?? readLayerStyleNumber(layer, 'aiConfidence');
}

export function isLayerPartialLoading(layer: Layer): boolean {
  const marker = readLayerStyleString(layer, 'tileHealth');
  if (marker === 'partial' || marker === 'degraded') return true;
  return readLayerStyleString(layer, 'loadingState') === 'partial';
}

export function isLayerLowAccuracy(layer: Layer): boolean {
  const marker = readLayerStyleString(layer, 'accuracy');
  if (marker === 'low' || marker === 'approximate') return true;
  return Boolean(layer.style?.['lowAccuracy']);
}

export function isLayerStale(layer: Layer, now: Date): boolean {
  const styleStamp = readLayerStyleString(layer, 'lastTiledAt');
  const source = styleStamp ?? layer.temporal?.end ?? null;
  if (!source) return false;
  const parsed = Date.parse(source);
  if (Number.isNaN(parsed)) return false;
  return now.getTime() - parsed > 1000 * 60 * 60 * 24 * 7;
}

export function layerStatuses(
  layer: Layer,
  options: { mixedCrs: boolean; now: Date; includeRestricted?: boolean },
): LayerStatus[] {
  const statuses: LayerStatus[] = [];
  if (options.mixedCrs) {
    statuses.push({
      key: 'mixed-crs',
      label: 'Mixed CRS',
      icon: 'language',
      variant: 'warning',
      detail: layer.crs,
    });
  }
  const confidence = layerConfidence(layer);
  if (confidence != null && confidence < 0.65) {
    statuses.push({
      key: 'low-confidence',
      label: 'Low confidence',
      icon: 'help',
      variant: 'warning',
      detail: `${Math.round(confidence * 100)}%`,
    });
  }
  if (isLayerExpiredCredentials(layer)) {
    statuses.push({
      key: 'expired-credentials',
      label: 'Credentials expired',
      icon: 'key',
      variant: 'error',
    });
  }
  if (options.includeRestricted !== false && isLayerRestricted(layer)) {
    statuses.push({
      key: 'restricted',
      label: 'Restricted',
      icon: 'policy',
      variant: 'error',
    });
  }
  if (isLayerPartialLoading(layer)) {
    statuses.push({
      key: 'partial-loading',
      label: 'Partial loading',
      icon: 'sync_problem',
      variant: 'warning',
    });
  }
  if (isLayerLowAccuracy(layer)) {
    statuses.push({
      key: 'low-accuracy',
      label: 'Low accuracy',
      icon: 'wrong_location',
      variant: 'warning',
    });
  }
  if (isLayerStale(layer, options.now)) {
    statuses.push({
      key: 'stale',
      label: 'Stale',
      icon: 'sync_problem',
      variant: 'warning',
    });
  }
  return statuses;
}

export function layerMatchesQuery(layer: Layer, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const label = (layer.label ?? '').toLowerCase();
  const id = layer.id.toLowerCase();
  const kind = layer.kind.toLowerCase();
  const provider = providerIdFromLayer(layer).toLowerCase();
  return label.includes(q) || id.includes(q) || kind.includes(q) || provider.includes(q);
}
